import type { PageGuide, GuideSection, GuideShot } from '../../components/guide/guideTypes'

type L = { ar: string; en: string; fr?: string }

export interface ScreenMeta {
  path: string
  label: L
  /** domain family for richer copy */
  domain?: string
}

const domainTips: Record<string, L[]> = {
  sales: [
    { ar: 'تأكد من اختيار المخزن الصحيح قبل الحفظ حتى يخصم الرصيد من المكان الصحيح.', en: 'Pick the correct store before saving so stock is deducted from the right place.', fr: 'Choisissez le bon dépôt avant d’enregistrer.' },
    { ar: 'يمكنك نسخ فاتورة سابقة لتسريع الإدخال المتكرر.', en: 'Copy a previous invoice to speed up repetitive entry.', fr: 'Copiez une facture précédente pour aller plus vite.' },
  ],
  purchase: [
    { ar: 'سعر التكلفة في الشراء يؤثر على متوسط تكلفة المخزون والتقارير.', en: 'Purchase cost affects inventory average cost and reports.', fr: 'Le coût d’achat impacte le coût moyen du stock.' },
    { ar: 'احفظ رقم فاتورة المورد للمطابقة لاحقاً.', en: 'Keep the vendor invoice number for later matching.', fr: 'Conservez le n° de facture fournisseur.' },
  ],
  inventory: [
    { ar: 'أي تسوية أو مناقلة تُسجَّل كحركة مخزنية — راجع الرصيد بعد الحفظ.', en: 'Any adjustment/transfer posts a stock movement — verify balance after save.', fr: 'Tout ajustement/transfert crée un mouvement — vérifiez le stock.' },
    { ar: 'الجرد الدوري يفضَّل إقفاله في نهاية الفترة بعد مطابقة العدّ الفعلي.', en: 'Close periodic stock take at period end after matching physical counts.', fr: 'Clôturez l’inventaire en fin de période après le comptage.' },
  ],
  accounting: [
    { ar: 'القيد لا يُحفظ إن لم يتساوَ المدين مع الدائن.', en: 'Entries will not save unless debits equal credits.', fr: 'Écriture impossible si débit ≠ crédit.' },
    { ar: 'اختر مركز التكلفة عند الحاجة لتقارير أدق.', en: 'Assign a cost center when needed for sharper reports.', fr: 'Attribuez un centre de coût si besoin.' },
  ],
  hr: [
    { ar: 'أكمل ملف الموظف والعقد قبل احتساب الحضور أو السلف.', en: 'Complete employee profile and contract before attendance or advances.', fr: 'Complétez le dossier et le contrat avant présence/avances.' },
    { ar: 'سياسات الحضور وجداول العمل تحدد التأخير والغياب تلقائياً.', en: 'Attendance policies and schedules drive lateness/absence automatically.', fr: 'Politiques et plannings pilotent retards/absences.' },
  ],
  reports: [
    { ar: 'حدد الفترة والفرع أولاً ثم اعرض — النتائج تعتمد على الفلاتر.', en: 'Set period and branch first, then view — results depend on filters.', fr: 'Période et succursale d’abord, puis affichage.' },
    { ar: 'صدّر Excel للمراجعة الخارجية أو الأرشفة.', en: 'Export Excel for external review or archiving.', fr: 'Exportez Excel pour revue ou archive.' },
  ],
  settings: [
    { ar: 'غيّر الإعدادات بحذر — تؤثر على كل المستخدمين والفواتير المطبوعة.', en: 'Change settings carefully — they affect all users and printed invoices.', fr: 'Modifiez les paramètres avec prudence.' },
    { ar: 'اختبر على مستخدم بصلاحية محدودة بعد تعديل الأدوار.', en: 'Test with a limited-permission user after changing roles.', fr: 'Testez avec un utilisateur à droits limités.' },
  ],
  municipality: [
    { ar: 'أدخل أنواع الخدمات/التراخيص قبل تسجيل الطلبات الفعلية.', en: 'Set up service/license types before recording live requests.', fr: 'Créez les types avant les demandes réelles.' },
    { ar: 'استخدم تقارير البلدية لمتابعة الإنجاز والتحصيل.', en: 'Use municipality reports to track progress and collections.', fr: 'Utilisez les rapports pour suivre avancement et recettes.' },
  ],
  restaurant: [
    { ar: 'اربط الطاولات والقائمة قبل استقبال الطلبات في الصالة أو التوصيل.', en: 'Link tables and menu before taking floor or delivery orders.', fr: 'Liez tables et menu avant les commandes.' },
    { ar: 'شاشة المطبخ تعرض الطلبات الحيّة — راقب حالة التجهيز.', en: 'Kitchen screen shows live orders — track prep status.', fr: 'L’écran cuisine montre les commandes en direct.' },
  ],
  dental: [
    { ar: 'سجّل المريض والخدمات أولاً ثم أنشئ الموعد أو فاتورة العلاج.', en: 'Register patient and services first, then appointment or treatment invoice.', fr: 'Patient et actes d’abord, puis rendez-vous/facture.' },
  ],
  subscriptions: [
    { ar: 'الباقات والعقود تسبق الفوترة الدورية والتنبيهات.', en: 'Packages and contracts come before recurring billing and alerts.', fr: 'Forfaits et contrats avant facturation et alertes.' },
  ],
  production: [
    { ar: 'فعّل الوصفة (نشطة) قبل أمر الإنتاج — المسودة لا تظهر في الأوامر.', en: 'Activate the BOM (Active) before orders — Drafts do not appear on orders.', fr: 'Activez la BOM avant les OF.' },
    { ar: 'عند السحب تُجرَّب بدائل المواد تلقائياً إن نقص الأصل.', en: 'On issue, material alternatives are tried automatically if primary is short.', fr: 'Alternatives auto si stock insuffisant.' },
    { ar: 'الاستلام يدعم كمية صالحة + تالف؛ الإكمال يُرجع المواد غير المستخدمة.', en: 'Receive supports good + scrap; complete returns unused materials.', fr: 'Réception PF + rebut; clôture retourne le surplus.' },
    { ar: 'لا تعدّل وصفة نشطة: أنشئ إصداراً جديداً ثم فعّله.', en: 'Do not edit an Active BOM: create a new version then activate.', fr: 'Nouvelle version puis activation.' },
  ],
  assets: [
    { ar: 'العمر الإنتاجي وطريقة الإهلاك يحددان قيود الإهلاك الشهرية.', en: 'Useful life and method drive monthly depreciation postings.', fr: 'Durée et méthode pilotent l’amortissement.' },
  ],
  default: [
    { ar: 'اضغط Ctrl+K للانتقال السريع لأي شاشة أخرى.', en: 'Press Ctrl+K to jump to any other screen quickly.', fr: 'Ctrl+K pour naviguer vite.' },
    { ar: 'إن ظهرت رسالة خطأ، اقرأها — غالباً حقل مطلوب أو صلاحية ناقصة.', en: 'If an error appears, read it — usually a required field or missing permission.', fr: 'Lisez les erreurs — champ requis ou droit manquant.' },
  ],
}

function detectDomain(path: string): string {
  if (path.startsWith('/reports')) return 'reports'
  if (path.startsWith('/municipality') || path.includes('/municipality')) return 'municipality'
  if (path.startsWith('/hr')) return 'hr'
  if (path.startsWith('/restaurant') || path === '/kitchen' || path === '/tables' || path === '/pos') return 'restaurant'
  if (path.startsWith('/dental') || path === '/patients' || path === '/appointments') return 'dental'
  if (path.startsWith('/subscriptions')) return 'subscriptions'
  if (path.startsWith('/production') || path.startsWith('/sorting')) return 'production'
  if (path.startsWith('/fixed-assets')) return 'assets'
  if (path.startsWith('/settings') || path === '/users' || path === '/roles' || path === '/permissions' || path === '/companies' || path === '/branches' || path === '/fiscal-years') return 'settings'
  if (
    path.includes('purchase') ||
    path.includes('vendor') ||
    path.includes('opening-stock')
  ) return 'purchase'
  if (
    path.includes('invoice') ||
    path.includes('sales') ||
    path.includes('quotation') ||
    path.includes('loyalty') ||
    path.includes('promotion') ||
    path.includes('sales-rep')
  ) return 'sales'
  if (
    path.includes('inventory') ||
    path.includes('store') ||
    path.includes('stock') ||
    path.includes('barcode') ||
    path.includes('warehouse') ||
    path.includes('item') ||
    path.includes('categor') ||
    path.includes('modifier')
  ) return 'inventory'
  if (
    path.includes('journal') ||
    path.includes('voucher') ||
    path.includes('chart-of-accounts') ||
    path.includes('cost-center') ||
    path.includes('budget') ||
    path.includes('bank') ||
    path.includes('tax') ||
    path.includes('operation-type') ||
    path.includes('currenc') ||
    path.includes('exchange')
  ) return 'accounting'
  return 'default'
}

function pageKind(path: string): 'list' | 'new' | 'edit' | 'report' | 'settings' | 'dashboard' {
  if (path.includes('/new') || path.endsWith('/create')) return 'new'
  if (path.includes('/edit') || path.includes('/copy')) return 'edit'
  if (path.startsWith('/reports') || path.includes('/report')) return 'report'
  if (path.includes('/settings') || path === '/permissions' || path === '/roles') return 'settings'
  if (path.includes('dashboard') || path === '/dashboard') return 'dashboard'
  return 'list'
}

function shot(motif: GuideShot['motif'], caption: L, highlight?: L): GuideShot {
  return { motif, caption, highlight }
}

function listSections(name: L): GuideSection[] {
  return [
    {
      title: { ar: '1) التعرف على الشاشة', en: '1) Know the screen', fr: '1) Comprendre' },
      steps: [
        {
          title: { ar: 'قائمة السجلات', en: 'Records list', fr: 'Liste' },
          body: {
            ar: `شاشة «${name.ar}» تعرض السجلات في جدول مرتّب. في الأعلى تجد زر الإضافة والبحث والفلاتر (تاريخ، حالة، فرع، مخزن…). ابدأ دائماً بتصفية النتائج قبل التعديل الجماعي.`,
            en: `“${name.en}” lists records in a sortable table. Top toolbar has Add, search, and filters (date, status, branch, store…). Filter first before bulk changes.`,
            fr: `Grille triable avec Ajouter, recherche et filtres. Filtrez d’abord.`,
          },
          shot: shot('list', { ar: 'جدول السجلات', en: 'Records grid', fr: 'Grille' }, { ar: 'ابحث ثم نفّذ إجراءً', en: 'Search then act', fr: 'Chercher puis agir' }),
        },
        {
          title: { ar: 'قراءة الأعمدة', en: 'Read the columns', fr: 'Colonnes' },
          body: {
            ar: 'كل عمود يوضح معلومة أساسية (رقم، اسم، تاريخ، مبلغ، حالة). الحالة الملوّنة تنبّهك إن كان السجل مسودة أو مرحّلاً أو ملغى.',
            en: 'Each column shows a key fact (number, name, date, amount, status). Colored status flags draft, posted, or cancelled.',
            fr: 'Colonnes clés et statuts colorés (brouillon, validé, annulé).',
          },
          shot: shot('dashboard', { ar: 'معنى الأعمدة', en: 'Column meaning', fr: 'Colonnes' }),
        },
      ],
    },
    {
      title: { ar: '2) العمليات اليومية', en: '2) Daily operations', fr: '2) Opérations' },
      steps: [
        {
          title: { ar: 'إضافة سجل', en: 'Add a record', fr: 'Ajouter' },
          body: {
            ar: 'اضغط إضافة/جديد. املأ الحقول الإلزامية (*)، راجع البنود إن وُجدت، ثم احفظ. بعض الشاشات تسمح بالاستيراد من Excel.',
            en: 'Click Add/New. Fill required (*) fields, review lines if any, then save. Some screens support Excel import.',
            fr: 'Ajouter, champs *, lignes, enregistrer. Import Excel parfois.',
          },
          shot: shot('form', { ar: 'بدء إدخال جديد', en: 'Start a new entry', fr: 'Nouvelle saisie' }),
        },
        {
          title: { ar: 'عرض · تعديل · نسخ · حذف', en: 'View · edit · copy · delete', fr: 'Voir · modifier · copier' },
          body: {
            ar: 'العين = عرض دون تعديل. القلم = تعديل. النسخ = سجل مشابه بسرعة. الحذف قد يطلب تأكيداً أو يحوّل السجل إلى غير نشط إن كان مستخدماً.',
            en: 'Eye = view only. Pencil = edit. Copy = similar record fast. Delete may confirm or soft-deactivate if in use.',
            fr: 'Œil, crayon, copie. Suppression confirmée ou désactivation.',
          },
          shot: shot('search', { ar: 'إجراءات الصف', en: 'Row actions', fr: 'Actions ligne' }),
        },
      ],
    },
    {
      title: { ar: '3) بعد الحفظ', en: '3) After saving', fr: '3) Après enregistrement' },
      steps: [
        {
          title: { ar: 'التحقق من الأثر', en: 'Verify the impact', fr: 'Vérifier l’impact' },
          body: {
            ar: 'إن كانت الشاشة ترحّل للمخزون أو الحسابات، افتح الرصيد أو كشف الحساب أو التقرير ذي الصلة بعد ثوانٍ للتأكد أن الترحيل نجح.',
            en: 'If the screen posts to stock or accounts, open the balance, statement, or related report shortly after to confirm posting.',
            fr: 'Vérifiez stock, relevé ou rapport après validation.',
          },
          shot: shot('approve', { ar: 'تأكيد النجاح', en: 'Confirm success', fr: 'Succès' }),
        },
        {
          title: { ar: 'طباعة ومشاركة', en: 'Print & share', fr: 'Imprimer' },
          body: {
            ar: 'استخدم أزرار الطباعة أو التصدير عند الحاجة للأرشفة أو للمراجعة الخارجية. يمكنك أيضاً Ctrl+K للانتقال لشاشة مرتبطة بسرعة.',
            en: 'Use print/export for archive or external review. Ctrl+K jumps to a related screen quickly.',
            fr: 'Impression/export, puis Ctrl+K vers un écran lié.',
          },
          shot: shot('print', { ar: 'طباعة أو تصدير', en: 'Print or export', fr: 'Export' }),
        },
      ],
    },
  ]
}

function formSections(name: L, kind: 'new' | 'edit'): GuideSection[] {
  const verbAr = kind === 'new' ? 'إنشاء' : 'تعديل'
  const verbEn = kind === 'new' ? 'Create' : 'Edit'
  return [
    {
      title: { ar: `${verbAr} — البيانات الأساسية`, en: `${verbEn} — basics`, fr: 'Données de base' },
      steps: [
        {
          title: { ar: 'الحقول الإلزامية', en: 'Required fields', fr: 'Champs obligatoires' },
          body: {
            ar: `في نموذج «${name.ar}» ابدأ بالحقول المعلّمة بـ *. القوائم المنسدلة تدعم البحث بالكتابة لتسريع الاختيار.`,
            en: `On the “${name.en}” form start with * required fields. Dropdowns support type-to-search.`,
            fr: `Commencez par les champs *. Les listes supportent la recherche.`,
          },
          shot: shot('form', { ar: 'نموذج الإدخال', en: 'Entry form', fr: 'Formulaire' }, { ar: 'املأ * أولاً', en: 'Fill * first', fr: '* d’abord' }),
        },
        {
          title: { ar: 'التفاصيل والبنود', en: 'Details & lines', fr: 'Détails' },
          body: {
            ar: 'إن وُجد جدول بنود (أصناف، مواد، أسطر قيد…) أضف سطراً لكل عنصر بمقادير صحيحة، ثم راجع الإجماليات أسفل النموذج.',
            en: 'If a lines grid exists (items, materials, journal lines…), add one row per element and review footer totals.',
            fr: 'Ajoutez une ligne par élément puis vérifiez les totaux.',
          },
          shot: shot('list', { ar: 'جدول البنود', en: 'Lines grid', fr: 'Lignes' }),
        },
      ],
    },
    {
      title: { ar: 'قبل الحفظ', en: 'Before saving', fr: 'Avant enregistrement' },
      steps: [
        {
          title: { ar: 'مراجعة سريعة', en: 'Quick review', fr: 'Contrôle rapide' },
          body: {
            ar: 'تحقق من التاريخ، الطرف (عميل/مورد)، المخزن أو الحساب، والمبالغ. خطأ صغير هنا ينعكس على التقارير لاحقاً.',
            en: 'Check date, party (customer/vendor), store/account, and amounts. Small mistakes show up later in reports.',
            fr: 'Vérifiez date, tiers, dépôt/compte et montants.',
          },
          shot: shot('search', { ar: 'نقاط المراجعة', en: 'Review points', fr: 'Contrôle' }),
        },
        {
          title: { ar: 'حفظ ومتابعة', en: 'Save & continue', fr: 'Enregistrer' },
          body: {
            ar: 'اضغط حفظ. بعدها قد تتاح الطباعة أو إنشاء سجل جديد أو العودة للقائمة.',
            en: 'Click Save. Then you can often print, create another, or return to the list.',
            fr: 'Enregistrez puis imprimez ou créez un autre.',
          },
          shot: shot('approve', { ar: 'أزرار الحفظ', en: 'Save actions', fr: 'Actions' }),
        },
      ],
    },
  ]
}

function reportSections(name: L): GuideSection[] {
  return [
    {
      title: { ar: 'تشغيل التقرير', en: 'Run the report', fr: 'Lancer le rapport' },
      steps: [
        {
          title: { ar: 'الفلاتر', en: 'Filters', fr: 'Filtres' },
          body: {
            ar: `لتقرير «${name.ar}»: حدّد من/إلى، الفرع، المخزن أو الحساب حسب المتاح، ثم اضغط عرض أو بحث.`,
            en: `For “${name.en}”: set from/to, branch, store or account as available, then View/Search.`,
            fr: `Période, succursale, dépôt/compte, puis Afficher.`,
          },
          shot: shot('search', { ar: 'شريط الفلاتر', en: 'Filter bar', fr: 'Barre de filtres' }),
        },
        {
          title: { ar: 'القراءة والتصدير', en: 'Read & export', fr: 'Lire et exporter' },
          body: {
            ar: 'راجع الأعمدة والمجاميع. استخدم طباعة أو تصدير Excel/PDF للأرشفة أو المشاركة.',
            en: 'Review columns and totals. Use print or Excel/PDF export to archive or share.',
            fr: 'Vérifiez colonnes et totaux, puis imprimez/exportez.',
          },
          shot: shot('print', { ar: 'تصدير وطباعة', en: 'Export & print', fr: 'Export' }),
        },
      ],
    },
  ]
}

function dashboardSections(name: L): GuideSection[] {
  return [
    {
      title: { ar: 'قراءة لوحة المعلومات', en: 'Reading the dashboard', fr: 'Lire le tableau de bord' },
      steps: [
        {
          title: { ar: 'المؤشرات', en: 'KPIs', fr: 'KPI' },
          body: {
            ar: `لوحة «${name.ar}» تلخّص الأرقام الأهم. مرّر على البطاقات والرسوم لفهم الاتجاهات خلال الفترة المعروضة.`,
            en: `“${name.en}” summarizes key numbers. Hover cards/charts to understand trends for the shown period.`,
            fr: `Résumé des chiffres clés et tendances sur la période.`,
          },
          shot: shot('dashboard', { ar: 'بطاقات المؤشرات', en: 'KPI cards', fr: 'Cartes KPI' }),
        },
        {
          title: { ar: 'الانتقال السريع', en: 'Quick jumps', fr: 'Raccourcis' },
          body: {
            ar: 'من اللوحة يمكنك فتح الشاشات المرتبطة (فواتير، تنبيهات، مهام). خصّص الودجات إن كان التخصيص متاحاً.',
            en: 'Jump to related screens (invoices, alerts, tasks). Customize widgets when available.',
            fr: 'Accédez aux écrans liés et personnalisez les widgets.',
          },
          shot: shot('settings', { ar: 'تخصيص العرض', en: 'Customize view', fr: 'Personnaliser' }),
        },
      ],
    },
  ]
}

function settingsSections(name: L): GuideSection[] {
  return [
    {
      title: { ar: 'ضبط الإعداد', en: 'Configure', fr: 'Configurer' },
      steps: [
        {
          title: { ar: 'افهم التأثير', en: 'Understand impact', fr: 'Comprendre l’impact' },
          body: {
            ar: `إعداد «${name.ar}» يؤثر على سلوك النظام للمستخدمين. غيّر قيمة واحدة، احفظ، ثم اختبر على شاشة تشغيلية.`,
            en: `“${name.en}” affects system behavior for users. Change one value, save, then test on an operational screen.`,
            fr: `Modifiez une valeur, enregistrez, puis testez.`,
          },
          shot: shot('settings', { ar: 'وحة الإعدادات', en: 'Settings panel', fr: 'Paramètres' }),
        },
        {
          title: { ar: 'الصلاحيات والأمان', en: 'Permissions & safety', fr: 'Droits et sécurité' },
          body: {
            ar: 'لا تمنح صلاحيات واسعة إلا للحاجة. بعد الحفظ سجّل خروجاً/دخولاً إن طُلب لتطبيق التغيير.',
            en: 'Grant wide permissions only when needed. Re-login if required after saving.',
            fr: 'Limitez les droits. Reconnectez-vous si demandé.',
          },
          shot: shot('approve', { ar: 'حفظ الإعداد', en: 'Save setting', fr: 'Enregistrer' }),
        },
      ],
    },
  ]
}

/** Build a detailed multi-step guide for any named screen */
export function buildDetailedGuide(meta: ScreenMeta): PageGuide {
  const domain = meta.domain ?? detectDomain(meta.path)
  const kind = pageKind(meta.path)
  const name = meta.label
  const tips = [...(domainTips[domain] ?? []), ...(domainTips.default ?? [])].slice(0, 3)

  let sections: GuideSection[]
  let emoji = '🧭'
  let accent = '#0d9488'

  if (kind === 'dashboard') {
    sections = dashboardSections(name)
    emoji = '🏠'
    accent = '#0891b2'
  } else if (kind === 'report') {
    sections = reportSections(name)
    emoji = '📊'
    accent = '#b45309'
  } else if (kind === 'settings') {
    sections = settingsSections(name)
    emoji = '⚙️'
    accent = '#475569'
  } else if (kind === 'new' || kind === 'edit') {
    sections = formSections(name, kind === 'edit' ? 'edit' : 'new')
    emoji = kind === 'new' ? '✨' : '✏️'
    accent = '#0e7490'
  } else {
    sections = listSections(name)
  }

  // Domain accent tweaks
  if (domain === 'sales') accent = '#b45309'
  if (domain === 'purchase') accent = '#047857'
  if (domain === 'accounting') accent = '#1d4ed8'
  if (domain === 'hr') accent = '#0f766e'
  if (domain === 'municipality') accent = '#0f766e'
  if (domain === 'restaurant') accent = '#c2410c'

  const kindLabel =
    kind === 'new'
      ? { ar: 'إدخال جديد', en: 'new entry', fr: 'nouvelle saisie' }
      : kind === 'edit'
        ? { ar: 'تعديل', en: 'editing', fr: 'modification' }
        : kind === 'report'
          ? { ar: 'تقرير', en: 'report', fr: 'rapport' }
          : kind === 'dashboard'
            ? { ar: 'لوحة معلومات', en: 'dashboard', fr: 'tableau de bord' }
            : kind === 'settings'
              ? { ar: 'إعدادات', en: 'settings', fr: 'paramètres' }
              : { ar: 'إدارة سجلات', en: 'records management', fr: 'gestion' }

  return {
    match: [meta.path, `${meta.path}*`],
    title: {
      ar: `شرح تفصيلي: ${name.ar}`,
      en: `Detailed guide: ${name.en}`,
      fr: `Guide détaillé : ${name.fr ?? name.en}`,
    },
    summary: {
      ar: `هذا دليل عملي لشاشة «${name.ar}» (${kindLabel.ar}). اتبع الخطوات بالترتيب مع الصور التوضيحية.`,
      en: `A practical walkthrough for “${name.en}” (${kindLabel.en}). Follow the steps with the illustrated shots.`,
      fr: `Parcours pratique pour «${name.fr ?? name.en}» (${kindLabel.fr}).`,
    },
    emoji,
    accent,
    gallery: [
      shot(
        kind === 'report' ? 'print' : kind === 'dashboard' ? 'dashboard' : kind === 'settings' ? 'settings' : kind === 'list' ? 'list' : 'form',
        { ar: `معاينة ${name.ar}`, en: `${name.en} preview`, fr: `Aperçu ${name.fr ?? name.en}` },
        { ar: 'ابدأ من هنا', en: 'Start here', fr: 'Commencez ici' }
      ),
    ],
    sections,
    tips,
  }
}
